// Route constants
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  
  COMMUNITY_INPUT: {
    BASE: '/',
    WELCOME: '/welcome',
    SEARCH: '/search',
    CONFIRM: '/confirm',
    SUBMIT: '/submit',
    DOWNLOAD_SALESFORCE: '/submit/download-salesforce',
  },
  
  ELISEGO: {
    BASE: '/elisego',
    NAME: '/elisego/name',
    AI_NAME: '/elisego/ai-name',
    VOICE_AREA_CODE: '/elisego/voice-area-code',
    SCHEDULE_TOURS: '/elisego/schedule-tours',
    SUBMIT: '/elisego/submit',
  },
  
  COMMUNITY_CHARACTERISTICS: {
    BASE: '/characteristics',
    NEXT_STEPS: '/characteristics/welcome',
    AFFORDABLE_INCOME: '/characteristics/income',
    SECTION_8: '/characteristics/section8',
    NEW_CONSTRUCTION: '/characteristics/new-construction',
    STUDENT: '/characteristics/student',
    SHARED_OPERATIONS: '/characteristics/shared-operations',
    SUBMIT_FOR_REVIEW: '/characteristics/submit',
  },
  IMPLEMENTATION_CONTACTS: {
    BASE: '/contacts',
    NEXT_STEPS: '/contacts/welcome',
    PROJECT_LEAD: '/contacts/project-lead',
    TECHNICAL: '/contacts/technical',
    VOICE: '/contacts/voice',
    KNOWLEDGE: '/contacts/knowledge',
    TRAINING: '/contacts/training',
    OWNER: '/contacts/owner',
    SUBMIT: '/contacts/submit',
    MARKETING: '/contacts/marketing',
  },
  
  NOT_FOUND: '/404',
  ERROR: '/error',
} as const;

export type NavPage = { 
  id: string; 
  label: string; 
  path: string;
  status?: 'not_started' | 'in_progress' | 'completed';
  enabled?: boolean;
};
export type NavSection = { 
  id: string; 
  label: string; 
  path: string; 
  pages: NavPage[];
  status?: 'not_started' | 'in_progress' | 'completed';
  enabled?: boolean;
};

export const SECTIONS: NavSection[] = [
  {
    id: 'community-input',
    label: 'Community Input',
    path: ROUTES.COMMUNITY_INPUT.BASE,
    pages: [
      { id: 'welcome', label: 'Welcome', path: ROUTES.COMMUNITY_INPUT.WELCOME },
      { id: 'search', label: 'Add & Review Communities', path: ROUTES.COMMUNITY_INPUT.SEARCH },
      { id: 'submit', label: 'Confirmation', path: ROUTES.COMMUNITY_INPUT.SUBMIT },
    ],
  },
  {
    id: 'elisego-configuration',
    label: 'EliseGO Configuration',
    path: ROUTES.ELISEGO.BASE,
    pages: [
      { id: 'plg-elisego-name', label: 'EliseGO Name', path: ROUTES.ELISEGO.NAME },
      { id: 'plg-ai-name', label: 'AI Name', path: ROUTES.ELISEGO.AI_NAME },
      { id: 'plg-voice-area-code', label: 'Leasing Voice Area Code', path: ROUTES.ELISEGO.VOICE_AREA_CODE },
      { id: 'plg-schedule-tours', label: 'Schedule Tours', path: ROUTES.ELISEGO.SCHEDULE_TOURS },
      { id: 'plg-submit', label: 'Submit for Review', path: ROUTES.ELISEGO.SUBMIT },
    ],
  },
  {
    id: 'community-characteristics',
    label: 'Community Characteristics',
    path: ROUTES.COMMUNITY_CHARACTERISTICS.BASE,
    pages: [
      { id: 'char-nextsteps', label: 'Next Steps', path: ROUTES.COMMUNITY_CHARACTERISTICS.NEXT_STEPS },
      { id: 'char-income', label: 'Income Restricted', path: ROUTES.COMMUNITY_CHARACTERISTICS.AFFORDABLE_INCOME },
      { id: 'char-section8', label: 'Section 8 / Housing Vouchers', path: ROUTES.COMMUNITY_CHARACTERISTICS.SECTION_8 },
      { id: 'char-newconstruction', label: 'New Construction', path: ROUTES.COMMUNITY_CHARACTERISTICS.NEW_CONSTRUCTION },
      { id: 'char-student', label: 'Student Housing', path: ROUTES.COMMUNITY_CHARACTERISTICS.STUDENT },
      { id: 'char-sharedops', label: 'Shared Operations', path: ROUTES.COMMUNITY_CHARACTERISTICS.SHARED_OPERATIONS },
      { id: 'char-submit', label: 'Submit for Review', path: ROUTES.COMMUNITY_CHARACTERISTICS.SUBMIT_FOR_REVIEW },
    ],
  },
  {
    id: 'implementation-contacts',
    label: 'Implementation Contacts',
    path: ROUTES.IMPLEMENTATION_CONTACTS.BASE,
    pages: [
      { id: 'impl-nextsteps', label: 'Next Steps', path: ROUTES.IMPLEMENTATION_CONTACTS.NEXT_STEPS },
      { id: 'impl-project-lead', label: 'Project Lead', path: ROUTES.IMPLEMENTATION_CONTACTS.PROJECT_LEAD },
      { id: 'impl-technical', label: 'Integrations / Technical', path: ROUTES.IMPLEMENTATION_CONTACTS.TECHNICAL },
      { id: 'impl-voice', label: 'Voice', path: ROUTES.IMPLEMENTATION_CONTACTS.VOICE },
      { id: 'impl-marketing', label: 'Marketing', path: ROUTES.IMPLEMENTATION_CONTACTS.MARKETING },
      { id: 'impl-knowledge', label: 'Community Knowledge', path: ROUTES.IMPLEMENTATION_CONTACTS.KNOWLEDGE },
      { id: 'impl-training', label: 'L&D / Training', path: ROUTES.IMPLEMENTATION_CONTACTS.TRAINING },
      { id: 'impl-owner', label: 'Ownership', path: ROUTES.IMPLEMENTATION_CONTACTS.OWNER },
      { id: 'impl-submit', label: 'Submit for Review', path: ROUTES.IMPLEMENTATION_CONTACTS.SUBMIT },
    ],
  },
];

export const getNavigationState = (pathname: string) => {
  for (const section of SECTIONS) {
    for (const page of section.pages) {
      if (pathname === page.path) {
        return { section: section.id, page: page.id };
      }
    }
  }
  return null;
};

export const getNextPage = (currentPath: string) => {
  const allPages = SECTIONS.flatMap(section => section.pages);
  const currentIndex = allPages.findIndex(page => page.path === currentPath);
  
  if (currentIndex >= 0 && currentIndex < allPages.length - 1) {
    return allPages[currentIndex + 1].path;
  }
  
  return null;
};

export const getPreviousPage = (currentPath: string) => {
  const allPages = SECTIONS.flatMap(section => section.pages);
  const currentIndex = allPages.findIndex(page => page.path === currentPath);
  
  if (currentIndex > 0) {
    return allPages[currentIndex - 1].path;
  }
  
  return null;
};
